import useUserProfile from "../useUserProfile";
import useActivePayee from "../useActivePayee";
import { ApolloError, FetchResult, useMutation } from "@apollo/client";
import {
  ACCEPT_TERMS_OF_SERVICE,
  DECLINE_TERMS_OF_SERVICE,
  UpdateTermsOfServiceAcceptVars,
  UpdateTermsOfServiceData,
  UpdateTermsOfServiceDeclineVars,
} from "common/graphql";
import { GSP_TOS_VERSION } from "common/constants";
import { CURRENT_TERMS_OF_SERVICE } from "@app.automotus.io/components/TermsOfService/versions";

export function useTermsOfService({ onError }: UseTermsOfServiceProps): UseTermsOfServiceResult {
  const { userProfile, loading: profileLoading, error: profileError } = useUserProfile();
  const { activePayee } = useActivePayee();
  const version =
    activePayee?.payeeAccountId === process.env.REACT_APP_GSP_AIRPORT_ACCOUNT_ID
      ? GSP_TOS_VERSION
      : CURRENT_TERMS_OF_SERVICE.version;
  const [acceptTermsOfService, { loading: acceptLoading, error: acceptError }] = useMutation<
    UpdateTermsOfServiceData,
    UpdateTermsOfServiceAcceptVars
  >(ACCEPT_TERMS_OF_SERVICE, {
    onError: onError ? (err) => onError("accept", err) : undefined,
  });
  const [declineTermsOfService, { loading: declineLoading, error: declineError }] = useMutation<
    UpdateTermsOfServiceData,
    UpdateTermsOfServiceDeclineVars
  >(DECLINE_TERMS_OF_SERVICE, {
    onError: onError ? (err) => onError("decline", err) : undefined,
  });

  function accept() {
    return acceptTermsOfService({
      variables: {
        userId: userProfile?.id || "",
        tosAcceptedVersion: version,
      },
    });
  }

  function decline() {
    return declineTermsOfService({
      variables: {
        userId: userProfile?.id || "",
      },
    });
  }

  return {
    accept,
    decline,
    isAccepted: !!userProfile?.tosAcceptedAt && !!userProfile?.tosAcceptedVersion,
    loading: profileLoading || acceptLoading || declineLoading,
    error: profileError || acceptError || declineError,
  };
}

export interface UseTermsOfServiceProps {
  onError?: (operation: "accept" | "decline", err: ApolloError) => void;
}

export interface UseTermsOfServiceResult {
  accept: () => Promise<FetchResult<UpdateTermsOfServiceData>>;
  decline: () => Promise<FetchResult<UpdateTermsOfServiceData>>;
  /**
   * True if the user has accepted any version of the Terms of Service.
   * This is a presence check only — callers that need to verify a specific version
   * should compare `userProfile.tosAcceptedVersion` directly.
   */
  isAccepted: boolean;
  loading: boolean;
  error?: ApolloError;
}

export default useTermsOfService;
